import { Component, Show, createSignal, onMount } from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { Button, Input, Spinner } from "../ui";
import { RepoPicker } from "../github/RepoPicker";
import { api, ApiError } from "../../api";
import { markOnboardingInFlight } from "../../stores/onboardingResume";

export interface LinkedRepoInfo {
  id: string;
  owner: string;
  name: string;
}

export interface GitHubWizardProps {
  projectId: string;
  onComplete: (repoInfo?: LinkedRepoInfo) => void;
  onSkip: () => void;
}

type WizardStep = "intro" | "link" | "success";

interface GitHubStatus {
  configured: boolean;
  mode: string;
  app_slug?: string;
}

interface AppInstallation {
  id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
}

interface InstallationListResponse {
  installations: AppInstallation[];
}

interface LinkedRepo {
  link: {
    id: string;
    repo_owner: string;
    repo_name: string;
    is_primary: boolean;
  };
  info: {
    full_name: string;
    description: string | null;
    html_url: string;
    private: boolean;
    language: string | null;
    default_branch: string;
  };
}

export const GitHubWizard: Component<GitHubWizardProps> = (props) => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = createSignal<WizardStep>("intro");
  const [githubConfigured, setGithubConfigured] = createSignal<boolean | null>(
    null,
  );
  const [loading, setLoading] = createSignal(true);
  const [appSlug, setAppSlug] = createSignal<string | null>(null);
  const [hasInstallation, setHasInstallation] = createSignal(false);
  const [installationMessage, setInstallationMessage] = createSignal("");

  const [owner, setOwner] = createSignal("");
  const [repo, setRepo] = createSignal("");
  const [linking, setLinking] = createSignal(false);
  const [error, setError] = createSignal("");
  const [linkedRepo, setLinkedRepo] = createSignal<LinkedRepo | null>(null);
  const [showManualFallback, setShowManualFallback] = createSignal(false);

  const checkGithubSetupResult = () => {
    const setupResult = searchParams.github_setup;
    if (setupResult === "success") {
      setInstallationMessage(
        "GitHub App installed successfully! You can now link a repository.",
      );
      setHasInstallation(true);
    } else if (setupResult === "cancelled") {
      setInstallationMessage(
        "GitHub App installation was cancelled. You can try again or skip.",
      );
    } else if (setupResult === "requested") {
      setInstallationMessage(
        "Installation request sent to your organization admin. You can continue once approved, or skip for now.",
      );
    }
  };

  onMount(async () => {
    try {
      const status = await api.get<GitHubStatus>("/github/status");
      setGithubConfigured(status.configured);

      if (status.app_slug) {
        setAppSlug(status.app_slug);
      }

      // If in App mode, check if user already has an installation
      if (status.mode === "App (installation token)") {
        try {
          const result = await api.get<InstallationListResponse>(
            "/github/app/installations",
          );
          if (result.installations.length > 0) {
            setHasInstallation(true);
          }
        } catch {
          // Non-fatal - user may not have installations yet
        }
      }

      checkGithubSetupResult();
    } catch {
      setGithubConfigured(false);
    } finally {
      setLoading(false);
    }
  });

  const isAppMode = () => appSlug() !== null;

  const handleInstallApp = () => {
    if (!appSlug()) return;

    // Save current location so callback page can redirect back
    localStorage.setItem("github_app_return_url", window.location.pathname);

    // Record which project onboarding was in the middle of, so the shell can
    // resume the wizard here instead of dropping the user on an empty board
    // (FLOW-991). This wizard is onboarding-only; ProjectSettingsPage has its
    // own install launcher and deliberately does not mark — there is no
    // onboarding flow to resume from there.
    markOnboardingInFlight(props.projectId);

    // Redirect via the backend initiate endpoint (FLOW-501): the backend
    // issues an HttpOnly install_state nonce cookie and redirects to GitHub.
    window.location.href = "/api/github/app/install";
  };

  const handleConnectRepository = () => {
    if (isAppMode() && !hasInstallation()) {
      handleInstallApp();
      return;
    }
    setStep("link");
  };

  const handleLink = async () => {
    const ownerVal = owner().trim();
    const repoVal = repo().trim();
    if (!ownerVal || !repoVal) {
      setError("Both owner and repository name are required");
      return;
    }

    setLinking(true);
    setError("");
    try {
      const result = await api.post<LinkedRepo>(
        `/projects/${props.projectId}/github/repos`,
        { owner: ownerVal, repo: repoVal, is_primary: true },
      );
      setLinkedRepo(result);
      setStep("success");
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setError("This repository is already linked to the project.");
        } else if (e.status === 404) {
          setError(
            `Repository "${ownerVal}/${repoVal}" was not found. Check the owner and name are correct.`,
          );
        } else if (e.status === 503) {
          setError(
            "GitHub integration is not configured on this instance. Please skip and set up later.",
          );
        } else {
          setError(e.message);
        }
      } else {
        setError(e instanceof Error ? e.message : "Failed to link repository");
      }
    } finally {
      setLinking(false);
    }
  };

  return (
    <div class="h-full flex items-center justify-center p-8">
      {/* Loading state */}
      <Show when={!loading()} fallback={<Spinner size="lg" />}>
        {/* Intro step */}
        <Show when={step() === "intro"}>
          <div class="w-full max-w-md text-center">
            <div class="mb-6">
              <div class="w-12 h-12 bg-ink rounded-md flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-6 h-6 text-paper"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-ink mb-2">
                Connect a GitHub repository
              </h1>
              <p class="text-ink-2">
                Link a repository so Flowicity can analyze your code, track pull
                requests, and provide smarter AI suggestions.
              </p>
            </div>

            {/* Installation status message */}
            <Show when={installationMessage()}>
              <div
                class={`rounded p-4 mb-6 text-left text-sm ${
                  searchParams.github_setup === "success"
                    ? "bg-signal-green-tint border border-signal-green/25 text-signal-green"
                    : searchParams.github_setup === "requested"
                      ? "bg-signal-blue-tint border border-signal-blue/25 text-signal-blue"
                      : "bg-signal-amber-tint border border-signal-amber/25 text-signal-amber"
                }`}
              >
                <p>{installationMessage()}</p>
              </div>
            </Show>

            <Show
              when={githubConfigured()}
              fallback={
                <div class="bg-signal-amber-tint border border-signal-amber/25 rounded p-4 mb-6 text-left">
                  <p class="text-sm text-signal-amber">
                    GitHub integration is not configured on this instance yet.
                    You can set this up later in project settings.
                  </p>
                </div>
              }
            >
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-left">
                <div class="bg-surface rounded border border-line p-3">
                  <div class="w-7 h-7 bg-sunken rounded flex items-center justify-center mb-1.5">
                    <svg
                      class="w-3.5 h-3.5 text-ink-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    </svg>
                  </div>
                  <h3 class="text-xs font-semibold text-ink mb-0.5">
                    Code Context
                  </h3>
                  <p class="text-xs text-ink-3">AI understands your codebase</p>
                </div>

                <div class="bg-surface rounded border border-line p-3">
                  <div class="w-7 h-7 bg-sunken rounded flex items-center justify-center mb-1.5">
                    <svg
                      class="w-3.5 h-3.5 text-ink-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-xs font-semibold text-ink mb-0.5">
                    PR Tracking
                  </h3>
                  <p class="text-xs text-ink-3">Auto-link PRs to work items</p>
                </div>

                <div class="bg-surface rounded border border-line p-3">
                  <div class="w-7 h-7 bg-sunken rounded flex items-center justify-center mb-1.5">
                    <svg
                      class="w-3.5 h-3.5 text-ink-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-xs font-semibold text-ink mb-0.5">
                    Smart Suggestions
                  </h3>
                  <p class="text-xs text-ink-3">Better estimates and impacts</p>
                </div>
              </div>
            </Show>

            <div class="flex flex-col gap-3">
              <Show when={githubConfigured()}>
                <Button
                  variant="primary"
                  size="lg"
                  class="w-full"
                  onClick={handleConnectRepository}
                >
                  {isAppMode() && !hasInstallation()
                    ? "Install GitHub App"
                    : "Connect Repository"}
                </Button>
                {/* In App mode with installation, also offer to install on more accounts */}
                <Show when={isAppMode() && hasInstallation()}>
                  <Button
                    variant="ghost"
                    size="md"
                    class="w-full"
                    onClick={handleInstallApp}
                  >
                    Install on another account
                  </Button>
                </Show>
              </Show>
              <Button
                variant={githubConfigured() ? "ghost" : "primary"}
                size={githubConfigured() ? "md" : "lg"}
                class="w-full"
                onClick={props.onSkip}
              >
                {githubConfigured()
                  ? "Skip for now"
                  : "Continue without GitHub"}
              </Button>
              <p class="text-xs text-ink-3">
                You can always connect a repository later in project settings.
              </p>
            </div>
          </div>
        </Show>

        {/* Link step */}
        <Show when={step() === "link"}>
          <div class="w-full max-w-md">
            <div class="text-center mb-6">
              <h1 class="text-2xl font-bold text-ink mb-2">
                Link a repository
              </h1>
              <p class="text-ink-2">
                Enter the GitHub owner and repository name to connect.
              </p>
            </div>

            <div class="bg-surface rounded border border-line p-6 space-y-4">
              <Show
                when={!showManualFallback()}
                fallback={
                  <>
                    <Input
                      label="Owner"
                      placeholder="e.g. my-org or my-username"
                      value={owner()}
                      onInput={setOwner}
                      maxLength={255}
                    />
                    <Input
                      label="Repository"
                      placeholder="e.g. my-app"
                      value={repo()}
                      onInput={setRepo}
                      maxLength={255}
                    />
                  </>
                }
              >
                <RepoPicker
                  projectId={props.projectId}
                  onSelect={(o, r) => {
                    setOwner(o);
                    setRepo(r);
                    handleLink();
                  }}
                  onFallback={() => setShowManualFallback(true)}
                />
              </Show>
              <Show when={error()}>
                <p class="text-sm text-signal-red">{error()}</p>
              </Show>
              <div class="flex gap-3 pt-2">
                <Button
                  variant="ghost"
                  class="flex-1"
                  onClick={() => {
                    setError("");
                    setShowManualFallback(false);
                    setStep("intro");
                  }}
                >
                  Back
                </Button>
                <Show when={showManualFallback()}>
                  <Button
                    variant="primary"
                    class="flex-1"
                    onClick={handleLink}
                    loading={linking()}
                    disabled={linking()}
                  >
                    Link Repository
                  </Button>
                </Show>
              </div>
            </div>
          </div>
        </Show>

        {/* Success step */}
        <Show when={step() === "success"}>
          <div class="w-full max-w-md text-center">
            <div class="mb-6">
              <div class="w-12 h-12 bg-signal-green-tint rounded-md flex items-center justify-center mx-auto mb-4">
                <svg
                  class="w-6 h-6 text-signal-green"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-ink mb-2">
                Repository connected!
              </h1>
              <p class="text-ink-2">
                Your repository has been linked to this project.
              </p>
            </div>

            <Show when={linkedRepo()}>
              {(repoData) => (
                <div class="bg-surface rounded border border-line p-4 mb-6 text-left">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-ink rounded flex items-center justify-center flex-shrink-0">
                      <svg
                        class="w-5 h-5 text-paper"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <p class="font-semibold text-ink truncate">
                        {repoData().info.full_name}
                      </p>
                      <Show when={repoData().info.description}>
                        <p class="text-sm text-ink-3 truncate">
                          {repoData().info.description}
                        </p>
                      </Show>
                      <div class="flex items-center gap-2 mt-1">
                        <Show when={repoData().info.language}>
                          <span class="text-xs text-ink-3">
                            {repoData().info.language}
                          </span>
                        </Show>
                        <span class="text-xs text-ink-3">
                          {repoData().info.private ? "Private" : "Public"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Show>

            <Button
              variant="primary"
              size="lg"
              class="w-full"
              onClick={() => {
                const repo = linkedRepo();
                if (repo) {
                  props.onComplete({
                    id: repo.link.id,
                    owner: repo.link.repo_owner,
                    name: repo.link.repo_name,
                  });
                } else {
                  props.onComplete();
                }
              }}
            >
              Continue
            </Button>
          </div>
        </Show>
      </Show>
    </div>
  );
};
